import { Fragment, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';

/**
 * The small slice of Markdown an event description actually uses.
 *
 * Paragraphs, **bold**, *italic*, `-` bullets, `1.` numbered lists, and links.
 * Nothing else — a heading or an image inside a listing would fight the
 * callout's own typography rather than help.
 *
 * **It builds React elements rather than HTML.** No `dangerouslySetInnerHTML`,
 * no sanitiser to keep correct, and nothing a source page writes can become
 * markup on our domain. That matters more here than anywhere else in the app:
 * this text is scraped from a few hundred third-party sites, and the whole
 * point of the field is to reproduce their formatting. An allow-list parser
 * gets the formatting without ever trusting the input.
 *
 * Links are rendered but forced through `rel="noopener noreferrer"`, and only
 * http(s) is followed — a `javascript:` URL in a listing renders as plain text.
 */

const Body = styled('div')({
  fontFamily: FONTS.body,
  fontSize: 14,
  lineHeight: 1.62,
  color: 'rgba(243,238,231,0.92)',
  '& > *:first-of-type': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
  '& p': { margin: '0 0 12px' },
  '& ul, & ol': { margin: '0 0 12px', paddingLeft: 20 },
  '& li': { margin: '0 0 5px' },
  '& li::marker': { color: 'rgba(243,238,231,0.45)' },
  '& strong': { fontWeight: 700, color: BRAND.parchment },
  '& a': {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    textDecorationColor: 'rgba(243,238,231,0.4)',
  },
  '& a:hover': { textDecorationColor: BRAND.parchment },
});

/** Only these ever become an anchor. Anything else stays as its own text. */
const SAFE_LINK = /^https?:\/\//i;

/**
 * Inline marks, innermost last so the outer split happens first.
 *
 * Bold before italic because `**` would otherwise be eaten by the single-`*`
 * rule and leave stray asterisks on screen.
 */
const INLINE = /(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_)/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!part) return null;

    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      const [, label, href] = link;
      if (!SAFE_LINK.test(href)) return <Fragment key={key}>{label}</Fragment>;
      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    }

    if (/^(\*\*|__).+\1$/.test(part)) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (/^(\*|_).+\1$/.test(part)) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }

    return <Fragment key={key}>{part}</Fragment>;
  });
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] };

/** Groups lines into paragraphs and runs of list items. */
function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];

  for (const rawLine of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const line = rawLine.trim();
    const last = blocks[blocks.length - 1];

    if (!line) {
      // A blank line ends whatever was open, which is what separates two
      // adjacent lists rather than merging them.
      if (last) blocks.push({ kind: 'p', lines: [] });
      continue;
    }

    const bullet = /^[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);

    if (bullet) {
      if (last?.kind === 'ul') last.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
      continue;
    }
    if (numbered) {
      if (last?.kind === 'ol') last.items.push(numbered[1]);
      else blocks.push({ kind: 'ol', items: [numbered[1]] });
      continue;
    }

    // A wrapped line continues its paragraph; anything else starts a new one.
    if (last?.kind === 'p' && last.lines.length > 0) last.lines.push(line);
    else blocks.push({ kind: 'p', lines: [line] });
  }

  return blocks.filter((b) => (b.kind === 'p' ? b.lines.length > 0 : b.items.length > 0));
}

interface Props {
  children: string;
  className?: string;
}

export default function MarkdownText({ children, className }: Props) {
  const blocks = parseBlocks(children);

  return (
    <Body className={className}>
      {blocks.map((block, i) => {
        if (block.kind === 'p') {
          return <p key={i}>{renderInline(block.lines.join(' '), `b${i}`)}</p>;
        }

        const List = block.kind === 'ul' ? 'ul' : 'ol';
        return (
          <List key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{renderInline(item, `b${i}-${j}`)}</li>
            ))}
          </List>
        );
      })}
    </Body>
  );
}
